import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Clock, Briefcase, CheckCircle2, AlertTriangle, Stethoscope } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

const DAY_START = 360;   // 06:00
const DAY_END = 1320;    // 22:00
const SPAN = DAY_END - DAY_START;
const pct = (m) => `${Math.max(0, Math.min(100, ((m - DAY_START) / SPAN) * 100))}%`;
const wpct = (a, b) => `${Math.max(0, Math.min(100, ((b - a) / SPAN) * 100))}%`;

const SKILL_LABELS = {
  vitals: "Vital Signs", phlebotomy: "Phlebotomy", poct: "POCT", rpm_devices: "RPM Setup",
  wound_care: "Wound Care", iv_therapy: "IV Therapy", catheter_care: "Catheter Care",
  medication_admin: "Med Admin", physiotherapy: "Physiotherapy", therapist: "Therapist",
};
const sl = (s) => SKILL_LABELS[s] || s;

function Timeline({ windows, busy }) {
  return (
    <div className="mt-2" data-testid="pf-timeline">
      <div className="relative h-6 rounded-md bg-[#F1F5F9] overflow-hidden">
        {/* working windows (open background) */}
        {(windows || []).map((w, i) => (
          <div key={`w${i}`} className="absolute top-0 bottom-0 bg-[#D1FAE5]"
               style={{ left: pct(w.start_min), width: wpct(w.start_min, w.end_min) }} title={`Open ${w.start}–${w.end}`} />
        ))}
        {/* booked blocks overlay */}
        {(busy || []).map((b, i) => (
          <div key={`b${i}`} className="absolute top-0 bottom-0 bg-[#94A3B8] border-x border-white/60"
               style={{ left: pct(b.start_min), width: wpct(b.start_min, b.end_min) }} title={`${b.label} ${b.start}–${b.end}`} />
        ))}
      </div>
      <div className="flex justify-between text-[9px] text-[#94A3B8] mt-0.5">
        {[6, 10, 14, 18, 22].map((h) => <span key={h}>{`${h}:00`}</span>)}
      </div>
    </div>
  );
}

function MatchChip({ ok, label }) {
  const style = ok === false
    ? { background: "#FEE2E2", color: "#991B1B" }
    : { background: "#DCFCE7", color: "#166534" };
  return <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={style}>{label}</span>;
}

export default function ProviderFinder({ open, onOpenChange, title, request, onPick }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !request) return;
    setLoading(true); setData(null);
    api.post("/fleet/provider-finder", request)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open, request]);

  const req = data?.required;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="provider-finder">
        <DialogHeader>
          <DialogTitle>{title || "Find a provider"}</DialogTitle>
          <DialogDescription>
            {req ? (
              <>Ranked by match → open slots → distance → workload.{" "}
                {req.role && <span className="capitalize">{req.role}</span>}
                {req.skills?.length > 0 && <> · skills: {req.skills.map(sl).join(", ")}</>}
                {(req.medical_specialty || req.sub_specialty) && <> · {[req.medical_specialty, req.sub_specialty].filter(Boolean).join(" / ")}</>}
              </>
            ) : "Loading requirement…"}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="p-8 text-center text-sm text-[#6B7280]">Searching providers…</div>
        ) : !data || data.candidates.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#6B7280]" data-testid="pf-empty">No providers of this role found.</div>
        ) : (
          <div className="space-y-3">
            {data.exact_count === 0 && (
              <div className="text-[12px] text-[#B45309] bg-[#FEF3C7] border border-[#FDE68A] rounded-lg px-3 py-2 inline-flex items-center gap-1.5" data-testid="pf-no-exact">
                <AlertTriangle className="w-4 h-4" /> No exact match available — showing closest partial matches.
              </div>
            )}
            {data.candidates.map((c) => (
              <div key={c.clinician_id} className={`rounded-xl border p-3 ${c.exact_match ? "border-[#A7F3D0]" : "border-[#FDE68A]"}`} data-testid={`pf-candidate-${c.clinician_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-[15px] font-semibold text-[#1F2937] flex items-center gap-2">
                      {c.name}
                      {c.exact_match
                        ? <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-[#DCFCE7] text-[#166534] inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />EXACT</span>
                        : <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-[#FEF3C7] text-[#92400E]">PARTIAL</span>}
                    </div>
                    <div className="text-[11px] text-[#94A3B8] capitalize">{(c.role_code || "").replace(/_/g, " ")}{c.facility_name ? ` · ${c.facility_name}` : ""}</div>
                  </div>
                  <div className="flex items-center gap-3 text-[12px] text-[#6B7280]">
                    {c.distance_km != null && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-[#0B7E8C]" />{c.distance_km} km · {c.eta_min}min</span>}
                    <span className="inline-flex items-center gap-1"><Briefcase className="w-3.5 h-3.5 text-[#0B7E8C]" />{c.workload} visit(s)</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5 mt-2">
                  {c.matched_skills.map((s) => <MatchChip key={s} ok label={sl(s)} />)}
                  {c.missing_skills.map((s) => <MatchChip key={s} ok={false} label={`✕ ${sl(s)}`} />)}
                  {c.medical_match != null && <MatchChip ok={c.medical_match} label={c.medical_specialization || "specialty"} />}
                  {c.sub_match != null && <MatchChip ok={c.sub_match} label={c.sub_specialty || "sub-specialty"} />}
                </div>

                <Timeline windows={c.windows} busy={c.busy} />

                {c.open_slots.length > 0 ? (
                  <div className="mt-2">
                    <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Open slots — click to assign</div>
                    <div className="flex flex-wrap gap-1.5">
                      {c.open_slots.slice(0, 18).map((s) => (
                        <button key={s.time} onClick={() => onPick(c.clinician_id, s.time, c)}
                          data-testid={`pf-slot-${c.clinician_id}-${s.time}`}
                          className="text-[12px] px-2 py-1 rounded-md border border-[#A7F3D0] bg-[#ECFDF5] text-[#065F46] hover:bg-[#D1FAE5] font-medium">
                          {s.time}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 text-[11px] text-[#B91C1C] inline-flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" /> {c.has_availability ? "Fully booked today" : "No availability set"}
                  </div>
                )}
                <div className="mt-2">
                  <button onClick={() => onPick(c.clinician_id, null, c)} data-testid={`pf-pick-${c.clinician_id}`}
                    className="text-[11px] text-[#0B7E8C] font-medium hover:underline inline-flex items-center gap-1">
                    <Stethoscope className="w-3.5 h-3.5" /> Select this provider
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
